/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */
import { capitalize, startCase } from 'lodash-es'
import { z } from 'zod'

import {
  COUNTRY_OPTIONS,
  DEGREE_OPTIONS,
  DOCX_FONT_SIZE_OPTIONS,
  DOCX_PAPER_SIZE_OPTIONS,
  DOCX_TEMPLATE_OPTIONS,
  FLUENCY_OPTIONS,
  HTML_FONT_SIZE_OPTIONS,
  HTML_TEMPLATE_OPTIONS,
  LANGUAGE_OPTIONS,
  LATEX_FONT_SIZE_OPTIONS,
  LATEX_FONTSPEC_NUMBERS_OPTIONS,
  LATEX_PAPER_SIZE_OPTIONS,
  LATEX_TEMPLATE_OPTIONS,
  LEVEL_OPTIONS,
  LINE_SPACING_OPTIONS,
  LOCALE_LANGUAGE_OPTIONS,
  NETWORK_OPTIONS,
  type ORDERABLE_SECTION_IDS,
} from '@/models'
import { joinNonEmptyString } from '@/utils'

/**
 * A type for all options.
 */
type Options =
  | typeof COUNTRY_OPTIONS
  | typeof DEGREE_OPTIONS
  | typeof DOCX_FONT_SIZE_OPTIONS
  | typeof DOCX_PAPER_SIZE_OPTIONS
  | typeof DOCX_TEMPLATE_OPTIONS
  | typeof HTML_FONT_SIZE_OPTIONS
  | typeof HTML_TEMPLATE_OPTIONS
  | typeof LATEX_FONTSPEC_NUMBERS_OPTIONS
  | typeof LATEX_FONT_SIZE_OPTIONS
  | typeof LATEX_TEMPLATE_OPTIONS
  | typeof FLUENCY_OPTIONS
  | typeof LANGUAGE_OPTIONS
  | typeof LINE_SPACING_OPTIONS
  | typeof LOCALE_LANGUAGE_OPTIONS
  | typeof LEVEL_OPTIONS
  | typeof NETWORK_OPTIONS
  | typeof LATEX_PAPER_SIZE_OPTIONS
  | typeof ORDERABLE_SECTION_IDS

/**
 * Creates an error message for an optionSchema
 *
 * @param options - The options to create a message for.
 * @param messagePrefix - The message prefix to use for the message.
 * @returns A message for an option schema.
 */
export function optionSchemaMessage(options: Options, messagePrefix: string) {
  return joinNonEmptyString(
    [
      `${messagePrefix} option is invalid,`,
      'it must be one of the following:',
      `[${options.map((option) => `"${option}"`).join(', ')}].`,
    ],
    ' '
  )
}

/**
 * Creates a zod schema for an option.
 *
 * @param options - The options to create a schema for.
 * @param messagePrefix - The message to use for the schema.
 * @returns A Zod schema for an option.
 */
export function optionSchema<T extends Options>(
  options: T,
  messagePrefix: string
) {
  return z
    .enum(options, {
      error: (issue) => {
        if (issue.input === undefined) {
          return {
            message: `${messagePrefix} option is required.`,
          }
        }

        return {
          message: optionSchemaMessage(options, messagePrefix),
        }
      },
    })
    .meta({
      title: `${capitalize(messagePrefix)} option`,
      description: `A predefined option from the available ${messagePrefix} choices.`,
    })
}

/**
 * Creates a zod schema for a string with a minimum and maximum length.
 *
 * @param name - The name of the string.
 * @param min - The minimum length of the string.
 * @param max - The maximum length of the string.
 * @returns A Zod schema for a string with a minimum and maximum length.
 */
export const SizedStringSchema = (name: string, min: number, max: number) => {
  return z
    .string({ message: `${name} is required.` })
    .min(min, { message: `${name} should be ${min} characters or more.` })
    .max(max, { message: `${name} should be ${max} characters or less.` })
}

/**
 * A zod schema for a country option.
 */
export const CountryOptionSchema = optionSchema(COUNTRY_OPTIONS, 'country')

/**
 * Creates a zod schema for a date string.
 *
 * A valid date string must be able to be parsed by `Date.parse`.
 *
 * @param date - The name of the date.
 * @returns A Zod schema for a date string.
 */
export function DateSchema(date: string) {
  return SizedStringSchema(date, 4, 32)
    .check((ctx) => {
      if (ctx.value.length < 4) {
        ctx.issues = [
          {
            code: 'too_small',
            input: ctx.value,
            minimum: 4,
            message: `${date} should be 4 characters or more.`,
            origin: 'string',
          },
        ]

        return
      }

      if (ctx.value.length > 32) {
        ctx.issues = [
          {
            code: 'too_big',
            input: ctx.value,
            maximum: 32,
            message: `${date} should be 32 characters or less.`,
            origin: 'string',
          },
        ]

        return
      }

      if (!Date.parse(ctx.value)) {
        ctx.issues = [
          {
            code: 'invalid_value',
            input: ctx.value,
            message: `${date} is invalid.`,
            origin: 'string',
            values: [ctx.value],
          },
        ]
      }
    })
    .meta({
      title: capitalize(startCase(date)),
      description: 'A valid date string that can be parsed by `Date.parse`.',
      examples: [
        '2025',
        '2025-01-01',
        'Jul 2025',
        'July 3, 2025',
        '2025-02-02T00:00:03.123Z',
      ],
    })
}

/**
 * A zod schema for a degree option.
 */
export const DegreeOptionSchema = optionSchema(DEGREE_OPTIONS, 'degree')

/**
 * A zod schema for DOCX fontFamily option in layout.
 */
export const DocxFontFamilySchema = z.string().meta({
  title: 'Font Family',
  description:
    'The font family to use in DOCX output. Should be a font name available on the system.',
  example: 'Arial',
})

/**
 * A zod schema for DOCX fontSize option in layout.
 */
export const DocxFontSizeOptionSchema = optionSchema(
  DOCX_FONT_SIZE_OPTIONS,
  'DOCX font size'
)

/**
 * A zod schema for a DOCX paper size option.
 */
export const DocxPaperSizeOptionSchema = optionSchema(
  DOCX_PAPER_SIZE_OPTIONS,
  'DOCX paper size'
)

/**
 * A zod schema for a DOCX template option.
 */
export const DocxTemplateOptionSchema = optionSchema(
  DOCX_TEMPLATE_OPTIONS,
  'DOCX template'
)

/**
 * An email schema used by various sections.
 */
export const EmailSchema = z.email({ message: 'email is invalid.' }).meta({
  id: 'email',
  title: 'Email',
  description: 'A valid email address.',
  examples: [
    'hi@ppresume.com',
    'first.last@company.org',
    'test+tag@domain.co.uk',
  ],
})

/**
 * A zod schema for a language fluency option.
 */
export const FluencyOptionSchema = optionSchema(FLUENCY_OPTIONS, 'fluency')

/**
 * A zod schema for HTML fontFamily option in layout.
 */
export const HtmlFontFamilySchema = z.string().meta({
  title: 'Font Family',
  description:
    'A comma-separated list of font families to use. The first available font will be used.',
  example: 'Monaco, Helvetica',
})

/**
 * A zod schema for HTML fontSize option in layout.
 */
export const HtmlFontSizeOptionSchema = optionSchema(
  HTML_FONT_SIZE_OPTIONS,
  'HTML font size'
)

/**
 * A zod schema for an HTML template option.
 */
export const HtmlTemplateOptionSchema = optionSchema(
  HTML_TEMPLATE_OPTIONS,
  'HTML template'
)

/**
 * A zod schema for a keywords array.
 */
export const KeywordsSchema = z
  .array(SizedStringSchema('keyword', 1, 32))
  .meta({
    id: 'keywords',
    title: 'Keywords',
    description: 'An array of keyword, each between 1 and 32 characters.',
    examples: [
      ['Javascript', 'React', 'Typescript'],
      ['Design', 'UI', 'UX'],
      ['Python', 'Data Science'],
    ],
  })

/**
 * A zod schema for a language.
 */
export const LanguageOptionSchema = optionSchema(LANGUAGE_OPTIONS, 'language')

/**
 * A zod schema for fontFamily option in layout.
 */
export const LatexFontFamilySchema = z.string().meta({
  title: 'Font Family',
  description:
    'A comma-separated list of font families to use. The first available font will be used.',
  example: 'Monaco, Helvetica',
})

/**
 * A zod schema for fontSize option in layout.
 */
export const LatexFontSizeOptionSchema = optionSchema(
  LATEX_FONT_SIZE_OPTIONS,
  'LaTeX font size'
)

/**
 * A zod schema for a font spec numbers style.
 */
export const LatexFontspecNumbersOptionSchema = optionSchema(
  LATEX_FONTSPEC_NUMBERS_OPTIONS,
  'LaTeX fontspec numbers'
)

/**
 * A zod schema for a template option.
 */
export const LatexTemplateOptionSchema = optionSchema(
  LATEX_TEMPLATE_OPTIONS,
  'LaTeX template'
)

/**
 * A zod schema for a line spacing option.
 *
 * Line spacing options are semantic values that map to engine-specific values:
 * - tight: Compact spacing for fitting more content
 * - normal: Balanced readability (default)
 * - relaxed: Comfortable reading with more breathing room
 * - loose: Very spacious layout
 */
export const LineSpacingOptionSchema = optionSchema(
  LINE_SPACING_OPTIONS,
  'line spacing'
)

/**
 * A zod schema for a locale language option.
 */
export const LocaleLanguageOptionSchema = optionSchema(
  LOCALE_LANGUAGE_OPTIONS,
  'locale language'
)

/**
 * A zod schema for a level option.
 */
export const LevelOptionSchema = optionSchema(LEVEL_OPTIONS, 'level')

/**
 * Creates a zod schema for a name.
 *
 * @param name - The name of the string.
 * @returns A Zod schema for a name string.
 */
export const NameSchema = (name: string) =>
  SizedStringSchema(name, 2, 128).meta({
    title: capitalize(name),
    description: `A ${name} between 2 and 128 characters.`,
    examples: ['Andy Dufresne', 'Xiao Hanyu', 'Jane Smith', 'Dr. Robert John'],
  })

/**
 * A zod schema for a network.
 */
export const NetworkOptionSchema = optionSchema(NETWORK_OPTIONS, 'network')

/**
 * A zod schema for a paper size option.
 */
export const PaperSizeOptionSchema = optionSchema(
  LATEX_PAPER_SIZE_OPTIONS,
  'LaTeX paper size'
)

/**
 * A regex for a phone number.
 */
const phoneNumberRegex = /^[+]?[(]?[0-9\s-]{1,15}[)]?[0-9\s-]{1,15}$/im

/**
 * A zod schema for a phone number.
 */
export const PhoneSchema = z
  .string()
  .regex(phoneNumberRegex, {
    message: 'phone number may be invalid.',
  })
  .meta({
    id: 'phone',
    title: 'Phone',
    description: joinNonEmptyString(
      [
        'A valid phone number that may include',
        'country code, parentheses, spaces, and hyphens.',
      ],
      ' '
    ),
    examples: ['555-123-4567', '+44 20 7946 0958', '(555) 123-4567'],
  })

/**
 * A zod schema for a summary.
 */
export const SummarySchema = SizedStringSchema('summary', 16, 1024).meta({
  id: 'summary',
  title: 'Summary',
  description: 'A summary text between 16 and 1024 characters.',
  examples: [
    'Experienced software engineer with 5+ years in full-stack development.',
    joinNonEmptyString(
      [
        'Creative designer passionate about',
        'user experience and modern design principles.',
      ],
      ' '
    ),
    joinNonEmptyString(
      [
        'Dedicated project manager with proven track record of',
        'delivering complex projects on time and budget.',
      ],
      ' '
    ),
  ],
})

/**
 * Creates a zod schema for an organization.
 *
 * @param name - The name of the organization.
 * @returns A Zod schema for an organization.
 */
export const OrganizationSchema = (name: string) =>
  SizedStringSchema(name, 2, 128).meta({
    title: capitalize(name),
    description: 'An organization name between 2 and 128 characters.',
    examples: [
      'Google Inc.',
      'Microsoft Corporation',
      'Startup XYZ',
      'Non-Profit Organization',
    ],
  })

/**
 * A zod schema for a url.
 */
export const UrlSchema = z
  .url({ message: 'URL is invalid.' })
  .max(256, { message: 'URL should be 256 characters or less.' })
  .meta({
    id: 'url',
    title: 'URL',
    description: 'A valid URL with maximum length of 256 characters.',
    examples: [
      'https://yamlresume.dev',
      'https://ppresume.com',
      'https://github.com/yamlresume/yamlresume',
      'https://linkedin.com/in/xiaohanyu1988',
      'https://www.example.com',
    ],
  })
